import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import usePDPPhotos from 'data-hooks/usePDPPhotos/index';
import usePDPCylindoViewer from 'data-hooks/usePDPCylindoViewer';
import useOnEsc from 'hooks/useOnEsc';
import usePDPViewGalleryImageEvent from 'data-hooks/useEvents/usePDPViewGalleryImageEvent';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import useRemoveBrokenImageUrls from 'global-state/brokenImageUrls/useRemoveBrokenImageUrls';
import RatioContainer from 'components/consumer/RatioContainer';
import JBProductImage from 'components/essentials/JBImage/JBProductImage';
import Placeholder from 'components/consumer/Placeholder';
import PDPCylindo3dViewer from 'components/consumer/PDP/PDPCylindo3dViewer';
import PDPDecideLaterOverlay from 'components/consumer/PDP/PDPDecideLaterOverlay';
import PDPGalleryLabelsAndViewingCount from 'components/consumer/PDP/PDPGalleryLabelsAndViewingCount';
import PDPFavoriteButton from 'components/consumer/PDPFavoriteButton';
import { markLuxPageNavigationEnd } from 'commons/lux';
import IconPlus from 'components/icons/iconPlus';
import IconMinus from 'components/icons/iconMinus';
import IconClose from 'components/icons/iconClose';
import { getMainImageImgixPropsForPhoto } from '../PDPDesktopHeroGallery/helpers/imgix-props';
import PDPDimensionShotViewer from '../PDPDimensionShotViewer';
import PDPProductIntroVideo from '../PDPDesktopHeroGallery/PDPProductIntroVideo';
import PDPGalleryRevampCircleButton from '../PDPDesktopGalleryRevamp/PDPGalleryRevampCircleButton';
import PDPGalleryRevampControlsBar from '../PDPDesktopGalleryRevamp/PDPGalleryRevampControlsBar';
import clickViewerControl from '../PDPDesktopGalleryRevamp/clickViewerControl';
import useCylindoARActive from '../hooks/useCylindoARActive';
import getMobileGallerySizes from './getMobileGallerySizes';
import PDPMobileGalleryRevampThumbRail from './PDPMobileGalleryRevampThumbRail';
import PDPMobileGalleryRevampExpandedGallery from './PDPMobileGalleryRevampExpandedGallery';
const REVAMP_VIEWER_ID = 'PDPMobileGalleryRevampViewer';

// Redesigned mobile gallery (CON-8444), behind ?galleryRevamp=1 for now
const PDPMobileGalleryRevamp = ({
  slug,
  productHeroImage = null,
  productCylindoHeroImage = null
}) => {
  const {
    loading,
    error,
    data,
    actions
  } = usePDPPhotos(null, slug);
  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();
  const {
    viewGalleryImageEvent
  } = usePDPViewGalleryImageEvent();
  const {
    cylindoFeatures,
    isCylindoAvailable,
    cylindoConfigurationPhotos,
    isCylindoConfigurationPhotos,
    isMechanicalOpen,
    toggleMechanicalOpen,
    dimensionShotUrls,
    currentDimensionShot,
    selectedDimensionShotIndex,
    hasMultipleDimensionShots,
    shouldShowDimensionThumbnail,
    goToNextDimensionShot,
    goToPreviousDimensionShot
  } = usePDPCylindoViewer({
    // isPDPDesktop only gates the 360 thumb urls + open/close state, not layout
    isPDPDesktop: true,
    deferCylindoFetch: true
  });
  const [isMeasureActive, setIsMeasureActive] = useState(false);
  const [expandedGalleryIndex, setExpandedGalleryIndex] = useState(null);
  const {
    isARActive,
    rearm: rearmARWatcher
  } = useCylindoARActive(REVAMP_VIEWER_ID);
  const {
    configurationPhotos: nonCylindoConfigurationPhotos,
    dimensionalImage,
    enableCrops,
    introVideoIds,
    isConfigFlipped,
    isVideoSelected,
    productName,
    selectedPhoto,
    studioPhotos: allStudioPhotos
  } = data ?? {};
  const {
    setSelectedPhoto,
    setIsVideoSelected
  } = actions ?? {};
  const configurationPhotos = useRemoveBrokenImageUrls(isCylindoConfigurationPhotos ? cylindoConfigurationPhotos : nonCylindoConfigurationPhotos);
  const studioPhotos = useRemoveBrokenImageUrls(allStudioPhotos);

  // selectPhoto clears isVideoSelected, so this drops back to the 360 spin
  const closeVideoView = useCallback(() => setSelectedPhoto?.(configurationPhotos[0] ?? null), [configurationPhotos, setSelectedPhoto]);
  const closeMeasureView = useCallback(() => setIsMeasureActive(false), []);
  const escapeTarget = isMeasureActive ? closeMeasureView : closeVideoView;
  useOnEsc((isMeasureActive || !!isVideoSelected) && expandedGalleryIndex == null, escapeTarget);
  const mainPhotoSizes = getMobileGallerySizes();
  const showDecideLaterOverlay = pdpSelectedCustomizerTab === 'decide-later';

  // Falls back to the static dimensional image when there are no Cylindo shots
  const currentDimensionShotToShow = useMemo(() => {
    if (currentDimensionShot) {
      return currentDimensionShot;
    }
    if (dimensionalImage?.src) {
      return {
        src: dimensionalImage.src,
        dimensionCode: 'static',
        id: 'static-dimensional'
      };
    }
    return null;
  }, [currentDimensionShot, dimensionalImage]);
  if (loading) {
    // Prefer Redis hero through Imgix. Cylindo SSR frames must stay as a plain
    // <img> (no Imgix params) to preserve byte-identity / valid LCP URL.
    const redisHeroSrc = productHeroImage?.src || null;
    const cylindoHeroSrc = !redisHeroSrc ? productCylindoHeroImage?.src || null : null;
    const redisHeroPhoto = redisHeroSrc ? {
      type: 'studio',
      src: redisHeroSrc
    } : null;
    return <div className="mb-4 relative" data-xc="PDPMobileGalleryRevamp">
        <RatioContainer>
          {redisHeroPhoto?.src ? <JBProductImage className="h-full w-full object-contain object-center select-none" {...getMainImageImgixPropsForPhoto(redisHeroPhoto)} lazy={false} priority /> : cylindoHeroSrc ? <img className="h-full w-full object-contain object-center select-none" src={cylindoHeroSrc} alt={productCylindoHeroImage?.alt || ''} width={productCylindoHeroImage?.width || 800} height={productCylindoHeroImage?.height || 800} loading="eager" fetchPriority="high" decoding="async" /> : <Placeholder type="rect" loading />}
        </RatioContainer>
      </div>;
  }
  if (!data) {
    return null;
  }
  if (error) {
    return 'Something went wrong, please try again';
  }
  const isExpandedGalleryOpen = expandedGalleryIndex != null;
  const introVideoId = introVideoIds?.desktop;
  const showMeasureView = isMeasureActive && !!currentDimensionShotToShow;
  const showVideoView = !showMeasureView && isVideoSelected && !!introVideoId;
  const showCylindoViewer = isCylindoAvailable && isCylindoConfigurationPhotos && !showMeasureView && !showVideoView;
  const fallbackPhoto = selectedPhoto?.src && selectedPhoto.type !== 'dimensional' ? selectedPhoto : configurationPhotos[0] ?? studioPhotos[0];
  const selectedConfigIndex = Math.max(0, configurationPhotos.findIndex(({
    src
  }) => src === selectedPhoto?.src));
  const isConfigPhotoSelected = selectedPhoto?.type === 'configuration' || selectedPhoto?.type === 'cylindo';
  return <div className="mb-4 relative" data-xc="PDPMobileGalleryRevamp">
      <div className="flex items-center gap-2 px-4 py-2">
        <div className="flex-1 min-w-0">
          <PDPMobileGalleryRevampThumbRail introVideoId={introVideoId} isVideoSelected={showVideoView} onExpand={setExpandedGalleryIndex} onSelectVideo={() => setIsVideoSelected?.()} photos={studioPhotos} viewGalleryImageEvent={viewGalleryImageEvent} />
        </div>

        <div className={classnames({
        hidden: isARActive
      })}>
          <PDPFavoriteButton />
        </div>
      </div>

      <div className="relative">
        <RatioContainer className={classnames('bg-transparent border-0 focus:outline-0',
      // Cylindo's AR panel ships a slate-blue backdrop; restyle it to Joybird
      // teal and keep its close button visible while our fabs stay hidden
      '[&_cylindo-viewer::part(fab)]:hidden [&_cylindo-viewer::part(qr-fab)]:!flex [&_cylindo-viewer::part(qr-backdrop)]:!bg-[rgba(16,124,140,0.8)] [&_cylindo-viewer::part(qr-dialog)]:border-2 [&_cylindo-viewer::part(qr-dialog)]:border-solid [&_cylindo-viewer::part(qr-dialog)]:border-brand')}>
          {showMeasureView && <>
              <PDPDimensionShotViewer currentDimensionShot={currentDimensionShotToShow} hasMultipleDimensionShots={hasMultipleDimensionShots} selectedDimensionShotIndex={selectedDimensionShotIndex} totalDimensionShots={dimensionShotUrls.length} onPrevious={goToPreviousDimensionShot} onNext={goToNextDimensionShot} previousArrowClass="left-2" nextArrowClass="right-2" arrowButtonClass="w-9 h-9 bg-white border border-solid border-gray-light6 hover:bg-gray-light9 [&_svg_path]:fill-gray-dark" className="h-full w-full" lazy={false} onLoad={markLuxPageNavigationEnd} />
              <div className="absolute flex flex-col gap-2 right-2 top-4 z-[2]">
                <PDPGalleryRevampCircleButton ariaLabel="Close measurements" onClick={() => setIsMeasureActive(false)}>
                  <IconClose width={9} height={9} />
                </PDPGalleryRevampCircleButton>
              </div>
            </>}

          {showVideoView && <>
              <PDPProductIntroVideo videoId={introVideoId} />
              <div className="absolute flex flex-col gap-2 right-2 top-4 z-[2]">
                <PDPGalleryRevampCircleButton ariaLabel="Close video" onClick={closeVideoView}>
                  <IconClose width={9} height={9} />
                </PDPGalleryRevampCircleButton>
              </div>
            </>}

          {!showMeasureView && !showVideoView && !showCylindoViewer && !!fallbackPhoto?.src && <JBProductImage className={classnames('h-full w-full object-contain object-center select-none', {
          'scale-x-[-1]': fallbackPhoto.type !== 'studio' && isConfigFlipped
        })} {...getMainImageImgixPropsForPhoto(fallbackPhoto, enableCrops)} sizes={mainPhotoSizes} lazy={false} priority onLoad={markLuxPageNavigationEnd} />}

          {isCylindoAvailable && <div className={classnames('w-full h-full [&_.mxt-btn-download]:hidden [&_.mxt-btn-expand]:hidden', {
          invisible: !showCylindoViewer
        })}>
              <PDPCylindo3dViewer containerId={REVAMP_VIEWER_ID} cylindoFeatures={cylindoFeatures} rotateIndex={isConfigPhotoSelected ? selectedConfigIndex : -1} />
            </div>}

          {showDecideLaterOverlay && <PDPDecideLaterOverlay />}
        </RatioContainer>

        <div className="absolute left-4 top-4 z-[2]">
          <PDPGalleryLabelsAndViewingCount mode="pdp" />
        </div>

        {showCylindoViewer && !isARActive && <div className="absolute flex flex-col gap-2 right-4 top-4 z-[2]">
            <PDPGalleryRevampCircleButton ariaLabel="Zoom in" onClick={() => clickViewerControl(REVAMP_VIEWER_ID, 'zoom-in')}>
              <IconPlus width={15} height={15} />
            </PDPGalleryRevampCircleButton>
            <PDPGalleryRevampCircleButton ariaLabel="Zoom out" onClick={() => clickViewerControl(REVAMP_VIEWER_ID, 'zoom-out')}>
              <IconMinus width={15} height={15} />
            </PDPGalleryRevampCircleButton>
          </div>}
      </div>

      <PDPGalleryRevampControlsBar isMeasureActive={showMeasureView}
    // Only one of measure / open can be active, so turning measure on
    // closes the mechanical view the same way opening clears measure
    onToggleMeasure={() => {
      if (!showMeasureView && isMechanicalOpen) {
        toggleMechanicalOpen();
      }
      setIsMeasureActive(!showMeasureView);
    }} showMeasure={shouldShowDimensionThumbnail} isMechanicalOpen={isMechanicalOpen} toggleMechanicalOpen={() => {
      // Last interaction wins: toggling open/close leaves measure view
      setIsMeasureActive(false);
      toggleMechanicalOpen();
    }} onLaunchViewerAR={isCylindoAvailable && isCylindoConfigurationPhotos ? () => {
      setIsMeasureActive(false);
      clickViewerControl(REVAMP_VIEWER_ID, 'view-in-ar');
      rearmARWatcher();
    } : null} />

      {/* Overlays the rail + viewer + controls area; viewer stays mounted underneath */}
      {isExpandedGalleryOpen && <div className="absolute inset-0 z-[3] bg-gray-light8">
          <PDPMobileGalleryRevampExpandedGallery enableCrops={enableCrops} initialIndex={expandedGalleryIndex} introVideoId={introVideoId} onClose={() => setExpandedGalleryIndex(null)} photos={studioPhotos} productName={productName} viewGalleryImageEvent={viewGalleryImageEvent} />
        </div>}
    </div>;
};
PDPMobileGalleryRevamp.propTypes = {
  slug: PropTypes.string,
  productHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  }),
  productCylindoHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  })
};
export default PDPMobileGalleryRevamp;